import {
  clientPaths,
  getEventSlugFromGamePath,
  isCustomerAppPath,
  isCustomerTabRoot as isCustomerTabRootPath,
  isEventGamePath,
  isPathPrefix,
  normalizePathname,
} from '@/utils/clientPaths';
import { hostPaths } from '@/utils/hostPaths';

export { normalizePathname, isCustomerTabRootPath as isCustomerTabRoot };

/** React Router stores stack index on history.state.idx when using a data/memory router. */
export function historyCanGoBack(): boolean {
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return typeof idx === 'number' && idx > 0;
}

export function isLoginRoute(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return path === '/login' || path === '/forgot-password' || path === '/check-email';
}

export function isCommunityTabRoot(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return path === '/host' || path === '/host/mabar';
}

export function isAppTabRoot(pathname: string): boolean {
  return isCustomerTabRootPath(pathname) || isCommunityTabRoot(pathname);
}

export function canIosSwipeBack(pathname: string): boolean {
  if (isLoginRoute(pathname)) return false;
  if (isAppTabRoot(pathname)) return false;
  return isCustomerAppPath(pathname) || pathname.startsWith('/host');
}

/**
 * Parent satu tingkat untuk path /host/mabar/* ketika history kosong.
 * Jangan default ke /statistic untuk dashboard/edit/details.
 */
export function getHostMabarBackFallback(pathname: string): string {
  const path = normalizePathname(pathname);

  if (path === hostPaths.mabar.create || path === hostPaths.mabar.list) {
    return hostPaths.mabar.list;
  }

  const gameMatch = path.match(/^\/host\/mabar\/game\/([^/]+)/);
  if (gameMatch) {
    const gSlug = gameMatch[1];
    const evSlug =
      sessionStorage.getItem(`mabar_event_slug_${gSlug}`) ||
      sessionStorage.getItem('last_mabar_event_slug');
    if (evSlug) return hostPaths.mabar.analitik(evSlug);
    return hostPaths.mabar.list;
  }

  // Legacy rekap tanpa event slug di path
  if (path.startsWith('/host/mabar/badminton/') || path.startsWith('/host/mabar/padel/')) {
    return hostPaths.mabar.list;
  }

  const mabarMatch = path.match(/^\/host\/mabar\/([^/]+)(?:\/(.*))?$/);
  if (!mabarMatch) return hostPaths.mabar.list;

  const slug = mabarMatch[1];
  const rest = mabarMatch[2] || '';

  if (slug === 'create') return hostPaths.mabar.list;

  const rootSegments = new Set([
    '',
    'statistic',
    'badminton',
    'padel',
    'pickleball',
    'dashboard',
    'edit',
    'details',
  ]);
  if (rootSegments.has(rest)) {
    return hostPaths.mabar.list;
  }

  if (rest.startsWith('badminton/')) return hostPaths.mabar.badminton(slug);
  if (rest.startsWith('padel/')) return hostPaths.mabar.padel(slug);
  if (rest.startsWith('pickleball/')) return hostPaths.mabar.pickleball(slug);
  if (rest.startsWith('game/')) return hostPaths.mabar.analitik(slug);

  return hostPaths.mabar.list;
}

export function getSwipeBackFallback(pathname: string): string {
  if (isEventGamePath(pathname)) {
    const slug = getEventSlugFromGamePath(pathname);
    if (slug) return clientPaths.event.statistik(slug);
  }
  if (isPathPrefix(pathname, clientPaths.event.list)) {
    const badmintonNested = pathname.match(/^\/event\/([^/]+)\/badminton\//);
    if (badmintonNested?.[1]) return clientPaths.event.badminton(badmintonNested[1]);
    const padelNested = pathname.match(/^\/event\/([^/]+)\/padel\//);
    if (padelNested?.[1]) return clientPaths.event.padel(padelNested[1]);
    return clientPaths.event.list;
  }
  if (isPathPrefix(pathname, clientPaths.discovery.root)) return clientPaths.discovery.root;
  if (isPathPrefix(pathname, clientPaths.booking.list)) return clientPaths.booking.list;
  if (isPathPrefix(pathname, clientPaths.profile.account)) return clientPaths.profile.settings;
  if (isPathPrefix(pathname, clientPaths.profile.password)) return clientPaths.profile.settings;
  if (isPathPrefix(pathname, clientPaths.profile.stats)) return clientPaths.profile.settings;
  if (isPathPrefix(pathname, clientPaths.transactions.list)) return clientPaths.profile.settings;
  if (isPathPrefix(pathname, clientPaths.myCommunity)) return clientPaths.home;
  if (isPathPrefix(pathname, '/community')) return clientPaths.discovery.community;
  if (pathname.startsWith('/host/mabar')) return getHostMabarBackFallback(pathname);
  if (pathname.startsWith('/host/profile-settings')) return '/host';
  if (isCustomerAppPath(pathname)) return clientPaths.home;
  if (pathname.startsWith('/host')) return '/host';
  return '/';
}

export function shouldExitOnHardwareBack(pathname: string): boolean {
  return isAppTabRoot(pathname) || isLoginRoute(pathname);
}
